import React, { useEffect, useState, useRef } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import { useTranslation } from 'react-i18next'

import OneOrManyFlights from '../one-or-many-flights'
import ContactForm2Column from '../contact-form/2-column'
import ThankYou from './thank-you'
import Spacer from './spacer'
import Cta from '../cta'
import Circles from '../circles'
import form from '../../../form'

const Steps = props => {
  const { t } = useTranslation()

  // current step manipulation
  const [currentStep, setCurrentStep] = useState(0)
  const goPrev = () => {
    setCurrentStep(currentStep - 1)
  }
  const goNext = () => {
    setCurrentStep(currentStep + 1)
  }

  // wrapper
  const wrapper = useRef(null)

  // modal - needs current step manipulation and wrapper
  const handleFirstStepSubmit = () => {
    disableBodyScroll(wrapper.current)
    if (props.onOpen) {
      props.onOpen()
    }
  }
  const handleCloseModal = () => {
    enableBodyScroll(wrapper.current)
    setCurrentStep(0)
    if (props.onClose) {
      props.onClose()
    }
  }
  useEffect(() => clearAllBodyScrollLocks, [])

  // steps data - need current step manipulation, wrapper and modal
  const steps = [
    {
      title: t('Flight Details'),
      eyebrow: t('Request your next flight'),
      element: (
        <OneOrManyFlights
          spacerClass='mb-24'
          button={{
            onClick: handleFirstStepSubmit,
            onClickInModal: goNext,
          }}
          withRightButton
        />
      ),
    },
    {
      title: t('Contact Information'),
      eyebrow: t('Contact Information'),
      element: (
        <Spacer>
          <ContactForm2Column />
          <div className='grid-x grid-margin-x align-justify align-middle pt-16 medium-pt-32'>
            <div className='shrink cell'>
              <Cta
                tag='button'
                attributes={{ type: 'button', onClick: goPrev }}
                reverse
              >
                <span className='show-for-medium'>{t('Go back')}</span>
              </Cta>
            </div>
            <button className='btn shrink large-6 cell' type='submit'>
              {t('Confirm')}
            </button>
          </div>
        </Spacer>
      ),
    },
    {
      title: t('Thank You!'),
      disableNavigation: true,
      wrapperStyle: {
        background: `url(${window.imagesPath}images/step-third-plane.png) center no-repeat`,
        backgroundSize: 'contain',
      },
      element: (
        <Spacer>
          <ThankYou />
        </Spacer>
      ),
    },
  ]
  const stepRefs = steps.map(() => useRef(null))
  useEffect(() => {
    form(wrapper.current, {
      onSuccess: () => setCurrentStep(2),
      skipCustomization: true,
      validation: {
        onInvalid: invalidNode => {
          const closestStep = invalidNode.closest('[data-step]')
          const index = stepRefs.map(el => el.current).indexOf(closestStep)
          if (index > -1) {
            setCurrentStep(index)
          }
        },
      },
    })
  }, [])

  const currentStepData = steps[currentStep]

  return (
    <div
      className='form-popup'
      style={currentStepData.wrapperStyle}
      ref={wrapper}
    >
      <div className='form-popup__vertical-align flex-container flex-dir-column align-center'>
        <div>
          <div className='grid-container'>
            <svg
              className='modal-close visible-in-modal'
              tabIndex='0'
              viewBox='0 0 16 16'
              role='button'
              onClick={handleCloseModal}
            >
              <path d='M1,1 L15,15 M15,1 L1,15' stroke='currentColor' />
            </svg>
            {currentStepData.eyebrow && (
              <h3 className='visible-in-modal text-center mb-40 subtitle'>
                {currentStepData.eyebrow}
              </h3>
            )}
            <div className='grid-x'>
              <div className='cell large-10 large-offset-1'>
                {!currentStepData.disableNavigation && (
                  <Spacer>
                    <ul className='grid-x grid-margin-x align-justify text-center mb-24 medium-mb-56 visible-in-modal steps-progress'>
                      {steps.map((step, index) => (
                        <li className='shrink cell' key={step.title}>
                          <span
                            className={classnames([
                              'step-toggle steps-progress__item-overlay',
                              {
                                locked: index > currentStep,
                                current: index === currentStep,
                              },
                            ])}
                          >
                            <div className='steps-progress__item'>
                              <Circles size={45} />
                            </div>
                            <div className='step-toggle__index'>
                              {index + 1}
                            </div>
                            <div className='pt-8 medium-pt-24 step-toggle__title'>
                              {step.title}
                            </div>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Spacer>
                )}

                <form
                  action={`${window.notificationScriptPath}handle-booking-form.php`}
                  className='form-animation'
                  data-focus-first-invalid
                  data-animated-submission
                  data-recaptcha-protected
                >
                  {steps.map((step, index) => (
                    <div
                      data-step
                      ref={stepRefs[index]}
                      key={step.title}
                      style={{
                        display: index === currentStep ? 'block' : 'none',
                      }}
                    >
                      {step.element}
                    </div>
                  ))}
                  <input
                    type='hidden'
                    value={document.documentElement.lang}
                    name='lang'
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Steps.propTypes = {
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
}

export default Steps
